/**
 * Storage Service Abstraction Layer
 *
 * Provides a testable abstraction over browser localStorage API.
 * Enables easy mocking in tests and potential future migration to other storage mechanisms.
 *
 * Benefits:
 * - Testability: Easy to mock storage in unit tests
 * - Type Safety: Automatic JSON serialization/deserialization with type checking
 * - Consistency: Centralized error handling for storage operations
 * - Flexibility: Easy to swap implementation (localStorage → sessionStorage → indexedDB)
 */

/**
 * Storage interface that any storage implementation must follow
 */
export interface IStorageService {
  /**
   * Gets a string value from storage
   * @param key - Storage key
   * @returns Value or null if not found
   */
  getItem(key: string): string | null

  /**
   * Sets a string value in storage
   * @param key - Storage key
   * @param value - Value to store
   */
  setItem(key: string, value: string): void

  /**
   * Removes an item from storage
   * @param key - Storage key
   */
  removeItem(key: string): void

  /**
   * Gets a parsed JSON object from storage
   * @param key - Storage key
   * @returns Parsed object or null if not found or invalid JSON
   */
  getObject<T>(key: string): T | null

  /**
   * Stores an object as JSON in storage
   * @param key - Storage key
   * @param value - Object to store
   */
  setObject<T>(key: string, value: T): void

  /**
   * Clears all items from storage
   */
  clear(): void

  /**
   * Gets all keys in storage
   */
  getAllKeys(): string[]
}

/**
 * Browser localStorage implementation
 */
export class BrowserStorageService implements IStorageService {
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch (error) {
      console.error(`Error reading from localStorage (key: ${key}):`, error)
      return null
    }
  }

  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value)
    } catch (error) {
      console.error(`Error writing to localStorage (key: ${key}):`, error)
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error(`Error removing from localStorage (key: ${key}):`, error)
    }
  }

  getObject<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key)
      if (!item) return null
      return JSON.parse(item) as T
    } catch (error) {
      console.error(`Error parsing JSON from localStorage (key: ${key}):`, error)
      return null
    }
  }

  setObject<T>(key: string, value: T): void {
    try {
      const serialized = JSON.stringify(value)
      localStorage.setItem(key, serialized)
    } catch (error) {
      console.error(`Error serializing to localStorage (key: ${key}):`, error)
    }
  }

  clear(): void {
    try {
      localStorage.clear()
    } catch (error) {
      console.error('Error clearing localStorage:', error)
    }
  }

  getAllKeys(): string[] {
    try {
      return Object.keys(localStorage)
    } catch (error) {
      console.error('Error getting localStorage keys:', error)
      return []
    }
  }
}

/**
 * In-memory storage implementation for testing
 */
export class MockStorageService implements IStorageService {
  private storage: Map<string, string> = new Map()

  getItem(key: string): string | null {
    return this.storage.get(key) ?? null
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value)
  }

  removeItem(key: string): void {
    this.storage.delete(key)
  }

  getObject<T>(key: string): T | null {
    const item = this.storage.get(key)
    if (!item) return null
    try {
      return JSON.parse(item) as T
    } catch {
      return null
    }
  }

  setObject<T>(key: string, value: T): void {
    try {
      const serialized = JSON.stringify(value)
      this.storage.set(key, serialized)
    } catch {
      // Silent fail for mock
    }
  }

  clear(): void {
    this.storage.clear()
  }

  getAllKeys(): string[] {
    return Array.from(this.storage.keys())
  }

  /**
   * Test utility: Get current storage state
   */
  getState(): Map<string, string> {
    return new Map(this.storage)
  }

  /**
   * Test utility: Set storage state
   */
  setState(state: Map<string, string>): void {
    this.storage = new Map(state)
  }
}

/**
 * Default storage service instance
 * Uses browser localStorage in production, can be replaced with mock in tests
 */
export const storageService: IStorageService = new BrowserStorageService()

/**
 * Creates a mock storage service for testing
 */
export const createMockStorageService = (): MockStorageService => {
  return new MockStorageService()
}
